// withNavigate.jsx
/**
 * withNavigate.jsx
 *
 * A higher-order component (HOC) that injects the `navigate` function from the React Router's `useNavigate` hook
 * into class components to enable programmatic navigation.
 *
 * This HOC is used in class components because `useNavigate` is a hook, which cannot be used directly in class components.
 * The `navigate` function is passed as a prop to the wrapped class component, allowing it to navigate between routes without
 * triggering a full page reload.
 *
 * Usage:
 * - Wrap your class component with `withNavigate` to access the `navigate` function for programmatic navigation.
 * - Replace `window.location.replace` or `history.push` with `this.props.navigate(<route>)` for navigation.
 * 
 * The `withNavigate` HOC is a workaround for class components to access the `navigate` function (which is derived from
 * `useNavigate` in React Router v6 and v7).
 *
 * * - **`withRouter`**: In React Router v6 and v7, **`withRouter`** was removed entirely. This HOC was previously used to inject 
 *   `history` into class components, but `useNavigate` now handles this need in functional components.
 * 
 * In summary, React Router v6 and v7 encourage the use of hooks in functional components, and this HOC allows class components 
 * to follow the new routing pattern without relying on legacy methods.
 */
import { useNavigate } from 'react-router-dom';

// Custom HOC to pass navigate as a prop to class components
const withNavigate = (WrappedComponent) => {
  return (props) => {
    const navigate = useNavigate();
    // const location = useLocation(); // Gets the current location object
    return <WrappedComponent {...props} navigate={navigate} />;
  };
};

export default withNavigate;
